import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Draggable from 'react-draggable';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Popover,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  ChevronLeft,
  ChevronRight,
  HelpOutline,
  OpenWith,
  PictureAsPdf,
} from '@mui/icons-material';
import { DialogTitleClose } from '../dialog';
import * as subMatFolderActions from '../../store/subMatFolder';
import { createPreviewObjectUrl } from '../G04docuSubMatFolder/SubMatFolderFileActions';
import {
  buildChildrenMap,
  COMPARISON_TOC_TITLE,
  comparisonSlotSourceKey,
  formatTocLabel,
  isVisibleTocItem,
  normalizeBodyHeaderPaging,
  normalizeTocNumbering,
  shouldIncludeComparisonInToc,
  tocDisplayTitle,
} from './subMatTemplateTocOrder';
import {
  CERTIFIED_STAMP_BORDER,
  CERTIFIED_STAMP_COLOR,
  CERTIFIED_STAMP_SIGN_OPACITY,
  STAMP_LAYOUT_COMPACT,
  STAMP_PDF_HEADER_HEIGHT_PT,
  STAMP_PDF_WIDTH_PT,
  normalizeStampLayout,
  stampPdfHeightPt,
  stampPdfSignCenterXPt,
  stampPdfWidthPt,
} from './certifiedStampSpec';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

/** PDF 기본 위치 — 페이지 좌하단 기준(미리보기·저장 공통) */
export const DEFAULT_STAMP_X_PT = 10;
export const DEFAULT_STAMP_Y_PT = 30;
/** PdfUtils.printCopyVerification 기본 양식 columnWidth */
export const STAMP_WIDTH_PT = STAMP_PDF_WIDTH_PT;
/**
 * 기본 양식 스탬프 테이블 대략 높이 (pt).
 * 미리보기 고스트는 실측 대비 약 70% 높이로 맞춤.
 */
export const STAMP_HEIGHT_PT = 77;
/** PdfUtils 인감 maxWidth */
export const STAMP_SIGN_MAX_WIDTH_PT = 50;
/** 헤더 행 대략 높이 (인감 세로 위치용) */
const STAMP_HEADER_HEIGHT_PT = STAMP_PDF_HEADER_HEIGHT_PT;

/** SubMatMergePdfService A4 본문 레이아웃과 동기화 */
const A4_WIDTH_PT = 595.276;
const A4_HEIGHT_PT = 841.89;
const MERGE_MARGIN_PT = 36;
const MERGE_HEADER_RESERVE_PT = 52;
const MERGE_FOOTER_RESERVE_PT = 56;
const MERGE_HEADER_COLOR = 'rgb(158,158,158)';
/** 머리글·바닥글 구분선 — 글자(158)보다 살짝 옅게 */
const MERGE_HEADER_RULE_COLOR = 'rgb(189,189,189)';
const MERGE_DOC_TYPE_LABEL = '자재승인서';

const PREVIEW_PAGE_WIDTH = 480;

const placementKey = (sourceKey, pageIndex, a4Frame) => (
  `${sourceKey}::${pageIndex}::${a4Frame ? 'a4' : 'raw'}`
);

const calcContainDraw = (placedW, placedH, boxW, boxH) => {
  if (placedW <= 0 || placedH <= 0 || boxW <= 0 || boxH <= 0) {
    return { drawW: 0, drawH: 0, rate: 0 };
  }
  const rate = Math.min(boxW / placedW, boxH / placedH);
  return { drawW: placedW * rate, drawH: placedH * rate, rate };
};

const itemHasDocument = (it) => (
  it
  && it.included !== false
  && Boolean(it.folderId)
  && it.hasCurrentFile === true
);

/** 포함·현재 PDF가 있는 자손이 있는지 (조상 섹션 표시용) */
const buildHasDocDescendantMap = (childrenMap) => {
  const cache = {};
  const visit = (itemId) => {
    if (cache[itemId] != null) return cache[itemId];
    const kids = (childrenMap[itemId] || []).filter((it) => it.included !== false);
    let found = false;
    kids.forEach((kid) => {
      if (itemHasDocument(kid) || visit(kid.id)) found = true;
    });
    cache[itemId] = found;
    return found;
  };
  (childrenMap[''] || []).forEach((root) => visit(root.id));
  Object.keys(childrenMap).forEach((k) => {
    if (k) visit(k);
  });
  return cache;
};

/**
 * 목차 번호·계층을 유지하되, 문서(PDF)가 있는 항목과 그 조상만 표시.
 * selectable=false 인 노드는 폴더(섹션) 라벨만.
 */
const buildPlacementTocRows = (items, extras, tocNumbering, extraSectionTitle) => {
  const style = normalizeTocNumbering(tocNumbering);
  const childrenMap = buildChildrenMap(items || []);
  const hasDocDesc = buildHasDocDescendantMap(childrenMap);
  const rows = [];
  const docs = [];

  const pushFolderRow = (kid, depth, path, isDoc) => {
    const title = tocDisplayTitle(kid) || kid.folderName || '서류';
    const sourceKey = isDoc ? (kid.placementSourceKey || `folder:${kid.folderId}`) : null;
    rows.push({
      rowKey: isDoc ? sourceKey : `section:${kid.id}`,
      sourceKey,
      kind: isDoc ? 'folder' : 'section',
      selectable: isDoc,
      folderId: isDoc ? kid.folderId : undefined,
      documentPath: isDoc ? (kid.documentPath || '') : '',
      itemId: kid.id,
      title,
      label: formatTocLabel(depth, path, title, style),
      depth,
    });
    if (isDoc) {
      docs.push({
        sourceKey,
        kind: 'folder',
        folderId: kid.folderId,
        documentPath: kid.documentPath || '',
        title,
        itemId: kid.id,
      });
    }
  };

  const walk = (parentId, depth, parentPath, startIndexOffset = 0) => {
    const kids = (childrenMap[parentId] || []).filter((it) => isVisibleTocItem(it, childrenMap));
    kids.forEach((kid, i) => {
      const path = [...parentPath, i + 1 + startIndexOffset];
      const isDoc = itemHasDocument(kid);
      const show = isDoc || hasDocDesc[kid.id];
      if (!show) return;
      pushFolderRow(kid, depth, path, isDoc);
      const comparisonChild = isDoc && shouldIncludeComparisonInToc(kid);
      if (comparisonChild) {
        const cmpPath = [...path, 1];
        const sourceKey = comparisonSlotSourceKey(kid.folderId);
        rows.push({
          rowKey: sourceKey,
          sourceKey,
          kind: 'folder',
          selectable: true,
          folderId: kid.folderId,
          itemId: `${kid.id}:comparison`,
          title: COMPARISON_TOC_TITLE,
          label: formatTocLabel(depth + 1, cmpPath, COMPARISON_TOC_TITLE, style),
          depth: depth + 1,
        });
        docs.push({
          sourceKey,
          kind: 'folder',
          folderId: kid.folderId,
          title: COMPARISON_TOC_TITLE,
          itemId: `${kid.id}:comparison`,
        });
      }
      walk(kid.id, depth + 1, path, comparisonChild ? 1 : 0);
    });
  };

  walk('', 0, []);

  const roots = (childrenMap[''] || []).filter((it) => isVisibleTocItem(it, childrenMap));
  const extraList = (extras || []).filter((ex) => ex && ex.included !== false && ex.file);
  if (extraList.length) {
    const rootIndex = roots.length + 1;
    const sectionTitle = (extraSectionTitle && String(extraSectionTitle).trim()) || '별도 첨부';
    rows.push({
      rowKey: 'section:extras',
      sourceKey: null,
      kind: 'section',
      selectable: false,
      title: sectionTitle,
      label: formatTocLabel(0, [rootIndex], sectionTitle, style),
      depth: 0,
    });
    extraList.forEach((ex, i) => {
      const title = (ex.title || ex.file?.name || `첨부${i + 1}`).replace(/\.pdf$/i, '');
      const sourceKey = `extra:${ex.id}`;
      rows.push({
        rowKey: sourceKey,
        sourceKey,
        kind: 'extra',
        selectable: true,
        extraId: ex.id,
        title,
        label: formatTocLabel(1, [rootIndex, i + 1], title, style),
        depth: 1,
        file: ex.file,
      });
      docs.push({
        sourceKey,
        kind: 'extra',
        extraId: ex.id,
        title,
        file: ex.file,
      });
    });
  }

  return { rows, docs };
};

/**
 * PDF 스탬프와 동일 배율(pt→px)의 외곽 박스 + 인감.
 * 헤더 문구·가로선은 생략. 본문은 드래그 아이콘만.
 */
const StampGhost = ({
  signThumbnailUrl,
  stampWidthPx,
  stampHeightPx,
  signMaxWidthPx,
  signCenterXPx,
  signCenterYPx,
  signColWidthPx = 0,
}) => (
  <Box
    sx={{
      width: stampWidthPx,
      height: stampHeightPx,
      border: CERTIFIED_STAMP_BORDER,
      boxSizing: 'border-box',
      bgcolor: 'rgba(255,255,255,0.88)',
      cursor: 'grab',
      userSelect: 'none',
      boxShadow: 2,
      position: 'relative',
      overflow: 'visible',
      '&:active': { cursor: 'grabbing' },
    }}
  >
    {signColWidthPx > 0 ? (
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: signColWidthPx,
          borderRight: CERTIFIED_STAMP_BORDER,
          pointerEvents: 'none',
        }}
      />
    ) : null}
    {signThumbnailUrl ? (
      <Box
        component="img"
        src={signThumbnailUrl}
        alt=""
        sx={{
          position: 'absolute',
          left: signCenterXPx,
          top: signCenterYPx,
          transform: 'translate(-50%, -50%)',
          maxWidth: signMaxWidthPx,
          width: 'auto',
          height: 'auto',
          opacity: CERTIFIED_STAMP_SIGN_OPACITY,
          pointerEvents: 'none',
        }}
      />
    ) : (
      <Box
        sx={{
          position: 'absolute',
          left: signCenterXPx,
          top: signCenterYPx,
          transform: 'translate(-50%, -50%)',
          width: signMaxWidthPx * 0.55,
          height: signMaxWidthPx * 0.55,
          border: `1px dashed ${CERTIFIED_STAMP_COLOR}`,
          borderRadius: '50%',
          opacity: 0.45,
          pointerEvents: 'none',
        }}
      />
    )}
    <Box
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 4,
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      <OpenWith
        sx={{
          fontSize: Math.max(14, stampWidthPx * 0.12),
          color: CERTIFIED_STAMP_COLOR,
          opacity: 0.55,
        }}
      />
    </Box>
  </Box>
);

/**
 * FE 전용 — 첨부 원본 PDF 미리보기 위에 원본대조필을 드래그로 배치.
 * bodyHeaderPaging 보임: A4+머리글/바닥글 프레임 위 좌표(합본과 동일 공간).
 * 안보임: 원본 페이지 좌표.
 */
const CertifiedStampPlacementDialog = ({
  open,
  onClose,
  items,
  extras,
  extraSectionTitle,
  tocNumbering,
  bodyHeaderPaging = true,
  headerLeft,
  signThumbnailUrl,
  placements,
  onChangePlacements,
  stampLayout = 'FULL',
}) => {
  const dispatch = useDispatch();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const previewPageWidth = isMobile
    ? Math.min(PREVIEW_PAGE_WIDTH, Math.max(240, (typeof window !== 'undefined' ? window.innerWidth : PREVIEW_PAGE_WIDTH) - 24))
    : PREVIEW_PAGE_WIDTH;
  const { rows, docs } = useMemo(
    () => buildPlacementTocRows(items, extras, tocNumbering, extraSectionTitle),
    [items, extras, tocNumbering, extraSectionTitle],
  );
  const useA4Frame = normalizeBodyHeaderPaging(bodyHeaderPaging);
  const headerLeftText = (headerLeft && String(headerLeft).trim()) || MERGE_DOC_TYPE_LABEL;
  const [selectedKey, setSelectedKey] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [pdfUrl, setPdfUrl] = useState('');
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState('');
  /** 스탬프 좌표 공간 (보임=A4, 안보임=원본) */
  const [pagePixelSize, setPagePixelSize] = useState({
    width: previewPageWidth,
    height: previewPageWidth * (A4_HEIGHT_PT / A4_WIDTH_PT),
  });
  const [pagePdfSize, setPagePdfSize] = useState({ width: A4_WIDTH_PT, height: A4_HEIGHT_PT });
  /** 콘텐츠 영역에 넣을 원본(세워진) 크기 pt */
  const [sourcePlacedPt, setSourcePlacedPt] = useState({ width: A4_WIDTH_PT, height: A4_HEIGHT_PT });
  /** react-pdf Page rotate. null이면 PDF /Rotate 그대로 측정 후 설정 */
  const [pageRotate, setPageRotate] = useState(null);
  const [applyHelpAnchor, setApplyHelpAnchor] = useState(null);
  const urlRef = useRef('');
  const dragNodeRef = useRef(null);

  const selected = docs.find((d) => d.sourceKey === selectedKey) || docs[0];

  const applyCanvasSize = useCallback((pdfW, pdfH) => {
    const w = previewPageWidth;
    const h = previewPageWidth * (pdfH / Math.max(pdfW, 1));
    setPagePdfSize((prev) => (
      prev.width === pdfW && prev.height === pdfH ? prev : { width: pdfW, height: pdfH }
    ));
    setPagePixelSize((prev) => (
      prev.width === w && prev.height === h ? prev : { width: w, height: h }
    ));
  }, [previewPageWidth]);

  useEffect(() => {
    if (!open) return;
    if (!docs.length) {
      setSelectedKey('');
      return;
    }
    if (!docs.some((d) => d.sourceKey === selectedKey)) {
      setSelectedKey(docs[0].sourceKey);
      setPageIndex(0);
    }
  }, [open, docs, selectedKey]);

  const revokeUrl = useCallback(() => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = '';
    }
  }, []);

  useEffect(() => () => revokeUrl(), [revokeUrl]);

  const selectedSourceKey = selected?.sourceKey || '';
  const selectedKind = selected?.kind || '';
  const selectedFolderId = selected?.folderId || '';
  const selectedDocumentPath = selected?.documentPath || '';
  const selectedFile = selected?.file || null;

  useEffect(() => {
    if (!open || !selectedSourceKey) {
      revokeUrl();
      setPdfUrl('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoadingPdf(true);
      setPdfError('');
      setNumPages(0);
      setPageIndex(0);
      setPageRotate(null);
      if (useA4Frame) {
        applyCanvasSize(A4_WIDTH_PT, A4_HEIGHT_PT);
      }
      try {
        let nextUrl = '';
        if (selectedKind === 'extra' && selectedFile) {
          nextUrl = URL.createObjectURL(selectedFile);
        } else if (selectedDocumentPath) {
          nextUrl = await createPreviewObjectUrl(selectedDocumentPath);
        } else if (selectedKind === 'folder' && selectedFolderId) {
          const files = await dispatch(subMatFolderActions.selectFiles(selectedFolderId));
          const current = (files || []).find((f) => f.isCurrent) || (files || [])[0];
          if (!current?.documentPath) {
            throw new Error('현재 사용 중인 PDF가 없습니다.');
          }
          nextUrl = await createPreviewObjectUrl(current.documentPath);
        } else {
          throw new Error('미리볼 파일이 없습니다.');
        }
        if (cancelled) {
          URL.revokeObjectURL(nextUrl);
          return;
        }
        revokeUrl();
        urlRef.current = nextUrl;
        setPdfUrl(nextUrl);
      } catch (e) {
        if (!cancelled) {
          revokeUrl();
          setPdfUrl('');
          setPdfError(e?.message || 'PDF를 불러오지 못했습니다.');
        }
      } finally {
        if (!cancelled) setLoadingPdf(false);
      }
    })();
    return () => { cancelled = true; };
  }, [
    open,
    selectedSourceKey,
    selectedKind,
    selectedFolderId,
    selectedDocumentPath,
    selectedFile,
    dispatch,
    revokeUrl,
    useA4Frame,
    applyCanvasSize,
  ]);

  const scale = pagePixelSize.width / Math.max(pagePdfSize.width, 1);
  const stampWidthPt = stampPdfWidthPt(stampLayout);
  const stampHeightPt = stampPdfHeightPt(stampLayout);
  const stampWpx = stampWidthPt * scale;
  const stampHpx = stampHeightPt * scale;
  const signMaxWidthPx = STAMP_SIGN_MAX_WIDTH_PT * scale;
  const signCenterXPt = stampPdfSignCenterXPt(stampLayout);
  const signCenterXPx = signCenterXPt * scale;
  const signCenterYPx = (normalizeStampLayout(stampLayout) === STAMP_LAYOUT_COMPACT
    ? stampHeightPt
    : STAMP_HEADER_HEIGHT_PT) / 2 * scale;
  const signColWidthPx = normalizeStampLayout(stampLayout) === STAMP_LAYOUT_COMPACT
    ? stampHpx
    : 0;

  const contentLayout = useMemo(() => {
    if (!useA4Frame) return null;
    const boxW = A4_WIDTH_PT - MERGE_MARGIN_PT * 2;
    const boxH = A4_HEIGHT_PT - MERGE_HEADER_RESERVE_PT - MERGE_FOOTER_RESERVE_PT;
    const { drawW, drawH } = calcContainDraw(
      sourcePlacedPt.width,
      sourcePlacedPt.height,
      boxW,
      boxH,
    );
    return {
      contentLeftPx: MERGE_MARGIN_PT * scale,
      contentTopPx: MERGE_HEADER_RESERVE_PT * scale,
      contentWidthPx: boxW * scale,
      contentHeightPx: boxH * scale,
      drawWidthPx: drawW * scale,
      drawHeightPx: drawH * scale,
      headerHeightPx: MERGE_HEADER_RESERVE_PT * scale,
      footerHeightPx: MERGE_FOOTER_RESERVE_PT * scale,
      marginPx: MERGE_MARGIN_PT * scale,
    };
  }, [useA4Frame, sourcePlacedPt, scale]);

  const activeKey = selected
    ? placementKey(selected.sourceKey, pageIndex, useA4Frame)
    : '';
  const currentPlacement = (placements && activeKey && placements[activeKey])
    || { xPt: DEFAULT_STAMP_X_PT, yPt: DEFAULT_STAMP_Y_PT };

  const dragPosition = {
    x: currentPlacement.xPt * scale,
    y: pagePixelSize.height - (currentPlacement.yPt * scale) - stampHpx,
  };

  const setPlacementPt = (xPt, yPt) => {
    if (!selected || !onChangePlacements) return;
    const maxX = Math.max(0, pagePdfSize.width - stampWidthPt);
    const maxY = Math.max(0, pagePdfSize.height - stampHeightPt);
    const next = {
      xPt: Math.min(maxX, Math.max(0, xPt)),
      yPt: Math.min(maxY, Math.max(0, yPt)),
    };
    onChangePlacements({
      ...(placements || {}),
      [placementKey(selected.sourceKey, pageIndex, useA4Frame)]: next,
    });
  };

  const handleDragStop = (_e, data) => {
    const xPt = data.x / scale;
    const yPt = (pagePixelSize.height - data.y - stampHpx) / scale;
    setPlacementPt(xPt, yPt);
  };

  const applyToAllPagesOfDoc = () => {
    if (!selected || numPages < 1) return;
    const base = currentPlacement;
    const next = { ...(placements || {}) };
    for (let i = 0; i < numPages; i += 1) {
      next[placementKey(selected.sourceKey, i, useA4Frame)] = { ...base };
    }
    onChangePlacements(next);
  };

  const handleSourcePageLoad = (page) => {
    const baseRot = page.rotate || 0;
    const vis = page.getViewport({ scale: 1, rotation: baseRot });
    const standTall = useA4Frame && vis.width > vis.height;
    const rot = standTall ? (baseRot + 90) % 360 : baseRot;
    const placed = page.getViewport({ scale: 1, rotation: rot });
    setPageRotate((prev) => (prev === rot ? prev : rot));
    setSourcePlacedPt((prev) => (
      prev.width === placed.width && prev.height === placed.height
        ? prev
        : { width: placed.width, height: placed.height }
    ));
    if (!useA4Frame) {
      applyCanvasSize(placed.width, placed.height);
    } else {
      applyCanvasSize(A4_WIDTH_PT, A4_HEIGHT_PT);
    }
  };

  const pageWidthPx = useA4Frame && contentLayout
    ? Math.max(1, contentLayout.drawWidthPx)
    : previewPageWidth;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      fullScreen={isMobile}
      maxWidth="lg"
      aria-labelledby="stamp-placement-dialog-title"
    >
      <DialogTitleClose id="stamp-placement-dialog-title" onClose={onClose}>
        원본대조필 위치 지정
      </DialogTitleClose>
      <DialogContent dividers sx={{ p: 0, minHeight: isMobile ? 0 : 520, overflow: 'auto' }}>
        <Box sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          height: '100%',
          minHeight: isMobile ? 0 : 520,
        }}
        >
          <Box
            sx={isMobile ? {
              width: '100%',
              flexShrink: 0,
              maxHeight: 180,
              borderBottom: 1,
              borderColor: 'divider',
              overflow: 'auto',
            } : {
              width: 280,
              flexShrink: 0,
              borderRight: 1,
              borderColor: 'divider',
              overflow: 'auto',
            }}
          >
            <Box sx={{ px: 1.5, py: 1 }}>
              <Typography variant="caption" color="text.secondary">
                목차
              </Typography>
            </Box>
            {!docs.length ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                현재 사용 중인 PDF가 있는 서류·별도 첨부가 없습니다.
              </Typography>
            ) : (
              <Box sx={{ px: 0.5, pb: 1 }}>
                {rows.map((row) => {
                  const selectedRow = row.selectable && selected?.sourceKey === row.sourceKey;
                  if (!row.selectable) {
                    return (
                      <Typography
                        key={row.rowKey}
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          px: 1,
                          py: 0.5,
                          pl: 1 + row.depth * 1.25,
                          fontWeight: 600,
                          fontSize: '0.8125rem',
                          lineHeight: 1.4,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={row.label}
                      >
                        {row.label}
                      </Typography>
                    );
                  }
                  return (
                    <Box
                      key={row.rowKey}
                      component="button"
                      type="button"
                      onClick={() => {
                        setSelectedKey(row.sourceKey);
                        setPageIndex(0);
                        setPageRotate(null);
                      }}
                      title={row.label}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        width: '100%',
                        textAlign: 'left',
                        border: 0,
                        borderRadius: 1,
                        cursor: 'pointer',
                        px: 1,
                        py: 0.6,
                        pl: 1 + row.depth * 1.25,
                        bgcolor: selectedRow ? 'action.selected' : 'transparent',
                        color: 'text.primary',
                        fontFamily: 'inherit',
                        fontSize: '0.8125rem',
                        lineHeight: 1.4,
                        '&:hover': { bgcolor: selectedRow ? 'action.selected' : 'action.hover' },
                      }}
                    >
                      <PictureAsPdf
                        sx={{
                          fontSize: 16,
                          color: 'error.main',
                          flexShrink: 0,
                          opacity: 0.85,
                        }}
                      />
                      <Box
                        component="span"
                        sx={{
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row.label}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: isMobile ? 360 : 0,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'rgba(0,0,0,0.04)',
            }}
          >
            <Box
              sx={{
                order: isMobile ? 2 : 0,
                px: 2,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                flexWrap: isMobile ? 'wrap' : 'nowrap',
                gap: 1,
                borderBottom: isMobile ? 0 : 1,
                borderTop: isMobile ? 1 : 0,
                borderColor: 'divider',
                bgcolor: '#fff',
                flexShrink: 0,
              }}
            >
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                {selected?.title || '—'}
              </Typography>
              <IconButton
                size="small"
                disabled={pageIndex <= 0}
                onClick={() => {
                  setPageRotate(null);
                  setPageIndex((p) => Math.max(0, p - 1));
                }}
                aria-label="이전 페이지"
              >
                <ChevronLeft />
              </IconButton>
              <Typography variant="body2" sx={{ minWidth: 64, textAlign: 'center' }}>
                {numPages ? `${pageIndex + 1} / ${numPages}` : '—'}
              </Typography>
              <IconButton
                size="small"
                disabled={!numPages || pageIndex >= numPages - 1}
                onClick={() => {
                  setPageRotate(null);
                  setPageIndex((p) => Math.min(numPages - 1, p + 1));
                }}
                aria-label="다음 페이지"
              >
                <ChevronRight />
              </IconButton>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <Button size="small" onClick={applyToAllPagesOfDoc} disabled={!numPages}>
                  이 문서 전체에 동일 적용
                </Button>
                <IconButton
                  size="small"
                  aria-label="동일 적용 도움말"
                  onClick={(e) => setApplyHelpAnchor(e.currentTarget)}
                  sx={{ p: 0.25 }}
                >
                  <HelpOutline fontSize="small" color="action" />
                </IconButton>
                <Popover
                  open={Boolean(applyHelpAnchor)}
                  anchorEl={applyHelpAnchor}
                  onClose={() => setApplyHelpAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  <Box sx={{ p: 1.5, maxWidth: 300 }}>
                    <Typography variant="body2" gutterBottom>
                      선택한 서류가 여러 페이지일 때, 지금 페이지에 지정한 원본대조필 위치를 그 서류의 모든 페이지에 동일하게 적용합니다.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      합본 전체가 아니라, 왼쪽 목차에서 고른 그 PDF에만 적용됩니다. 페이지마다 위치가 다르면 각각 지정하세요.
                    </Typography>
                  </Box>
                </Popover>
              </Box>
            </Box>

            <Box
              sx={{
                order: isMobile ? 1 : 0,
                flex: 1,
                overflow: 'auto',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                p: isMobile ? 1 : 2,
              }}
            >
              {loadingPdf ? (
                <CircularProgress size={36} sx={{ mt: 6 }} />
              ) : pdfError ? (
                <Typography color="error" sx={{ mt: 4 }}>{pdfError}</Typography>
              ) : pdfUrl ? (
                <Box
                  sx={{
                    position: 'relative',
                    width: pagePixelSize.width,
                    height: pagePixelSize.height,
                    boxShadow: 3,
                    bgcolor: '#fff',
                    lineHeight: 0,
                    overflow: 'hidden',
                  }}
                >
                  {useA4Frame && contentLayout ? (
                    <>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: contentLayout.headerHeightPx,
                          px: `${contentLayout.marginPx}px`,
                          boxSizing: 'border-box',
                          zIndex: 1,
                          pointerEvents: 'none',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-end',
                            height: '100%',
                            pb: `${8 * scale}px`,
                            borderBottom: `0.8px solid ${MERGE_HEADER_RULE_COLOR}`,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 9 * scale,
                              color: MERGE_HEADER_COLOR,
                              lineHeight: 1.2,
                              maxWidth: '48%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {headerLeftText}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 9 * scale,
                              color: MERGE_HEADER_COLOR,
                              lineHeight: 1.2,
                              maxWidth: '48%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              textAlign: 'right',
                            }}
                          >
                            {selected?.title || ''}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          position: 'absolute',
                          left: contentLayout.contentLeftPx,
                          top: contentLayout.contentTopPx,
                          width: contentLayout.contentWidthPx,
                          height: contentLayout.contentHeightPx,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          bgcolor: '#fafafa',
                        }}
                      >
                        <Document
                          file={pdfUrl}
                          loading={<CircularProgress size={28} />}
                          onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                          onLoadError={() => setPdfError('PDF를 표시하지 못했습니다.')}
                        >
                          <Page
                            key={`${pageIndex}-a4`}
                            pageNumber={pageIndex + 1}
                            width={pageWidthPx}
                            {...(pageRotate != null ? { rotate: pageRotate } : {})}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                            onLoadSuccess={handleSourcePageLoad}
                          />
                        </Document>
                      </Box>

                      <Box
                        sx={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          bottom: 0,
                          height: contentLayout.footerHeightPx,
                          px: `${contentLayout.marginPx}px`,
                          boxSizing: 'border-box',
                          zIndex: 1,
                          pointerEvents: 'none',
                        }}
                      >
                        <Box
                          sx={{
                            borderTop: `0.8px solid ${MERGE_HEADER_RULE_COLOR}`,
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pt: `${4 * scale}px`,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 9 * scale,
                              color: MERGE_HEADER_COLOR,
                              lineHeight: 1,
                            }}
                          >
                            {numPages ? `${pageIndex + 1} / ${numPages}` : '—'}
                          </Typography>
                        </Box>
                      </Box>
                    </>
                  ) : (
                    <Document
                      file={pdfUrl}
                      loading={<CircularProgress size={28} sx={{ m: 4 }} />}
                      onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                      onLoadError={() => setPdfError('PDF를 표시하지 못했습니다.')}
                    >
                      <Page
                        key={`${pageIndex}-raw`}
                        pageNumber={pageIndex + 1}
                        width={previewPageWidth}
                        {...(pageRotate != null ? { rotate: pageRotate } : {})}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        onLoadSuccess={handleSourcePageLoad}
                      />
                    </Document>
                  )}

                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      overflow: 'hidden',
                      zIndex: 2,
                    }}
                  >
                    <Draggable
                      nodeRef={dragNodeRef}
                      bounds="parent"
                      position={dragPosition}
                      onStop={handleDragStop}
                    >
                      <Box ref={dragNodeRef} sx={{ position: 'absolute', top: 0, left: 0 }}>
                        <StampGhost
                          signThumbnailUrl={signThumbnailUrl}
                          stampWidthPx={stampWpx}
                          stampHeightPx={stampHpx}
                          signMaxWidthPx={signMaxWidthPx}
                          signCenterXPx={signCenterXPx}
                          signCenterYPx={signCenterYPx}
                          signColWidthPx={signColWidthPx}
                        />
                      </Box>
                    </Draggable>
                  </Box>
                </Box>
              ) : null}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CertifiedStampPlacementDialog;
export { placementKey, buildPlacementTocRows };
